import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import FicheHeader from "./FicheHeader";
import AuthImg from "./AuthImg";
import { CLAIM_TYPE_LABELS, CLAIM_STATUS_META } from "./FleetEventsView";
import { api, formatEURDecimal, formatDateFR, formatApiError } from "../../lib/api";
import { confirmAction } from "../ConfirmDialog";
import { toast } from "sonner";
import { Loader2, Camera, FileText, X, Lock, Unlock, ExternalLink } from "lucide-react";

const RESP_LABELS = {
  client: "Client",
  tiers: "Tiers",
  vendor: "Loueur",
  shared: "Partagée",
  unknown: "À déterminer",
};

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 outline-none";
const labelCls = "text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500";

export default function ClaimFiche({ claimId, onBack }) {
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [acting, setActing] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/vendor/claims/${claimId}`)
      .then(({ data }) => setClaim(data.claim))
      .catch(() => setClaim(null))
      .finally(() => setLoading(false));
  }, [claimId]);

  const patch = async (payload, msg) => {
    setActing(true);
    try {
      const { data } = await api.patch(`/vendor/claims/${claimId}`, payload);
      setClaim(data.claim);
      if (msg) toast.success(msg);
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
      return false;
    } finally {
      setActing(false);
    }
  };

  const startEdit = () => {
    setForm({
      claim_type: claim.claim_type,
      date_of_loss: claim.date_of_loss || "",
      description: claim.description || "",
      driver_name: claim.driver_name || "",
      client_name: claim.client_name || "",
      client_email: claim.client_email || "",
      estimated_amount: claim.estimated_amount ?? "",
      invoiced_amount: claim.invoiced_amount ?? "",
      responsibility: claim.responsibility || "unknown",
    });
    setEditing(true);
  };

  const save = async () => {
    const ok = await patch({
      ...form,
      estimated_amount: form.estimated_amount === "" ? null : parseFloat(form.estimated_amount),
      invoiced_amount: form.invoiced_amount === "" ? null : parseFloat(form.invoiced_amount),
      driver_name: form.driver_name || null,
      client_name: form.client_name || null,
      client_email: form.client_email || null,
    }, "Sinistre mis à jour");
    if (ok) setEditing(false);
  };

  const uploadFile = async (kind, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vendor/claims/${claimId}/file?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(kind === "photo" ? "Photo ajoutée" : "Document ajouté");
      const { data } = await api.get(`/vendor/claims/${claimId}`);
      setClaim(data.claim);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };

  const deleteFile = async (kind, path) => {
    try {
      await api.delete(`/vendor/claims/${claimId}/file?kind=${kind}&path=${encodeURIComponent(path)}`);
      const { data } = await api.get(`/vendor/claims/${claimId}`);
      setClaim(data.claim);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };

  const openDoc = async (path) => {
    try {
      const res = await api.get(`/files/${path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Impossible d'ouvrir le document");
    }
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;
  if (!claim) return <div className="py-16 text-center text-sm text-slate-500">Sinistre introuvable.</div>;

  const st = CLAIM_STATUS_META[claim.status] || CLAIM_STATUS_META.open;
  const isClosed = claim.status === "closed";
  const ins = claim.insurance || {};

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="claim-fiche">
      <FicheHeader
        onBack={onBack}
        backLabel="Retour aux sinistres"
        title={`Sinistre ${claim.reference}`}
        subtitle={`${claim.vehicle_name} · ${CLAIM_TYPE_LABELS[claim.claim_type] || claim.claim_type} · ${formatDateFR(claim.date_of_loss)}`}
        badge={
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${st.cls}`} data-testid="claim-status-badge">
            {st.label}
          </span>
        }
        editing={editing}
        onEdit={isClosed ? undefined : startEdit}
        onCancel={() => setEditing(false)}
        onSave={save}
        saving={acting}
        actions={
          !editing && (
            isClosed ? (
              <button onClick={() => patch({ status: "in_progress" }, "Sinistre rouvert")} disabled={acting}
                className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="claim-reopen-btn">
                <Unlock className="h-3.5 w-3.5" /> Rouvrir
              </button>
            ) : (
              <button onClick={async () => (await confirmAction({ title: "Clôturer ce sinistre ?", message: "Le dossier sinistre deviendra définitivement non modifiable.", confirmLabel: "Confirmer la clôture", variant: "warning" })) && patch({ status: "closed" }, "Sinistre clôturé")} disabled={acting}
                className="h-10 px-4 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="claim-close-btn">
                <Lock className="h-3.5 w-3.5" /> Clôturer
              </button>
            )
          )
        }
        testid="claim-fiche-header"
      />

      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <div className="lg:col-span-2 space-y-5">
          {/* ─── Détails ─── */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="claim-details">
            <div className="overline mb-4">Détails du sinistre</div>
            {editing ? (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Type</label>
                  <select value={form.claim_type} onChange={(e) => setForm({ ...form, claim_type: e.target.value })} className={inputCls + " mt-1"} data-testid="claim-edit-type">
                    {Object.entries(CLAIM_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Date du sinistre</label>
                  <input type="date" value={form.date_of_loss} onChange={(e) => setForm({ ...form, date_of_loss: e.target.value })} className={inputCls + " mt-1"} data-testid="claim-edit-date" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Description</label>
                  <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls + " mt-1 resize-none"} data-testid="claim-edit-description" />
                </div>
                <div>
                  <label className={labelCls}>Responsabilité</label>
                  <select value={form.responsibility} onChange={(e) => setForm({ ...form, responsibility: e.target.value })} className={inputCls + " mt-1"} data-testid="claim-edit-responsibility">
                    {Object.entries(RESP_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Conducteur</label>
                  <input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} className={inputCls + " mt-1"} data-testid="claim-edit-driver" />
                </div>
                <div>
                  <label className={labelCls}>Client</label>
                  <input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className={inputCls + " mt-1"} data-testid="claim-edit-client-name" />
                </div>
                <div>
                  <label className={labelCls}>Email client</label>
                  <input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className={inputCls + " mt-1"} data-testid="claim-edit-client-email" />
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                {claim.description ? (
                  <p className="text-slate-700 whitespace-pre-wrap" data-testid="claim-description-text">{claim.description}</p>
                ) : (
                  <p className="text-slate-400 italic">Aucune description.</p>
                )}
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 pt-3 border-t border-slate-100">
                  <Info l="Responsabilité" v={RESP_LABELS[claim.responsibility] || "—"} />
                  <Info l="Conducteur" v={claim.driver_name || "—"} />
                  <Info l="Client" v={claim.client_name || "—"} />
                  <Info l="Email client" v={claim.client_email || "—"} />
                </div>
              </div>
            )}
          </div>

          {/* ─── Photos ─── */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="claim-photos">
            <div className="flex items-center justify-between mb-4">
              <div className="overline">Photos ({(claim.photos || []).length})</div>
              {!isClosed && (
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer" data-testid="claim-add-photo-btn">
                  <Camera className="h-3.5 w-3.5" /> Ajouter
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadFile("photo", e.target.files[0]); e.target.value = ""; }} />
                </label>
              )}
            </div>
            {(claim.photos || []).length === 0 ? (
              <div className="text-sm text-slate-400">Aucune photo.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {claim.photos.map((p, idx) => (
                  <div key={idx} className="relative group">
                    <AuthImg path={p} className="h-24 w-24 rounded-xl object-cover border border-slate-200 cursor-pointer" onClick={() => openDoc(p)} />
                    {!isClosed && (
                      <button onClick={() => deleteFile("photo", p)} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white hidden group-hover:flex items-center justify-center" data-testid={`claim-photo-delete-${idx}`}>
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ─── Documents ─── */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="claim-documents">
            <div className="flex items-center justify-between mb-4">
              <div className="overline">Documents ({(claim.documents || []).length})</div>
              {!isClosed && (
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer" data-testid="claim-add-document-btn">
                  <FileText className="h-3.5 w-3.5" /> Ajouter
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadFile("document", e.target.files[0]); e.target.value = ""; }} />
                </label>
              )}
            </div>
            {(claim.documents || []).length === 0 ? (
              <div className="text-sm text-slate-400">Aucun document (constat, devis garage, courrier assurance…).</div>
            ) : (
              <div className="space-y-2">
                {claim.documents.map((d, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl border border-slate-200" data-testid={`claim-document-${idx}`}>
                    <button onClick={() => openDoc(d.path)} className="flex items-center gap-2 min-w-0 text-sm text-slate-700 hover:text-violet-700">
                      <FileText className="h-4 w-4 text-violet-600 shrink-0" />
                      <span className="truncate">{d.name || "Document"}</span>
                      <ExternalLink className="h-3 w-3 text-slate-400 shrink-0" />
                    </button>
                    {!isClosed && (
                      <button onClick={() => deleteFile("document", d.path)} className="h-6 w-6 rounded-md hover:bg-red-50 text-red-500 inline-flex items-center justify-center shrink-0" data-testid={`claim-document-delete-${idx}`}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ─── Colonne latérale ─── */}
        <div className="space-y-4">
          {/* Statut */}
          {!isClosed && !editing && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="claim-status-card">
              <div className="overline mb-3">Statut</div>
              <select
                value={claim.status}
                onChange={(e) => patch({ status: e.target.value }, "Statut mis à jour")}
                className={inputCls}
                data-testid="claim-status-select"
              >
                <option value="open">Déclaré</option>
                <option value="in_progress">En cours</option>
                <option value="waiting_insurer">Attente assurance</option>
              </select>
            </div>
          )}

          {/* Montants */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="claim-amounts">
            <div className="overline mb-3">Montants</div>
            {editing ? (
              <div className="space-y-3">
                <div>
                  <label className={labelCls}>Montant estimé (€)</label>
                  <input type="number" min="0" step="0.01" value={form.estimated_amount} onChange={(e) => setForm({ ...form, estimated_amount: e.target.value })} className={inputCls + " mt-1"} data-testid="claim-edit-estimated" />
                </div>
                <div>
                  <label className={labelCls}>Montant facturé (€)</label>
                  <input type="number" min="0" step="0.01" value={form.invoiced_amount} onChange={(e) => setForm({ ...form, invoiced_amount: e.target.value })} className={inputCls + " mt-1"} data-testid="claim-edit-invoiced" />
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <Info l="Estimé" v={claim.estimated_amount != null ? formatEURDecimal(claim.estimated_amount) : "—"} />
                <Info l="Facturé" v={claim.invoiced_amount != null ? formatEURDecimal(claim.invoiced_amount) : "—"} />
                <div className="text-[10px] text-slate-400 pt-1">La refacturation client sera disponible avec le module Facturation Phase 2.</div>
              </div>
            )}
          </div>

          {/* Assurance (snapshot) */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="claim-insurance">
            <div className="overline mb-3">Assurance & franchise</div>
            <div className="space-y-2 text-sm">
              <Info l="Assureur" v={ins.insurer || "—"} />
              <Info l="N° de police" v={ins.policy_number || "—"} />
              <Info l="Franchise" v={ins.franchise_amount ? formatEURDecimal(ins.franchise_amount) : "—"} />
              <div className="text-[10px] text-slate-400 pt-1">Figé à la déclaration du sinistre (Paramétrages → Assurance / Légal).</div>
            </div>
          </div>

          {/* Liens */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="claim-links">
            <div className="overline mb-3">Liens</div>
            <div className="space-y-2 text-sm">
              <Link to={`/vendor/pilotage?tab=vehicles&vehicle=${claim.vehicle_id}`} className="block text-violet-600 hover:underline" data-testid="claim-vehicle-link">
                Véhicule : {claim.vehicle_name}
              </Link>
              {claim.booking_id && (
                <Link to={`/vendor/pilotage?tab=bookings&booking=${claim.booking_id}`} className="block text-violet-600 hover:underline" data-testid="claim-booking-link">
                  Réservation {claim.booking_reference || ""}
                </Link>
              )}
              {claim.source_issue_id && (
                <div className="text-slate-600" data-testid="claim-source-issue">
                  Issu du signalement <span className="font-semibold">{claim.source_issue_reference || ""}</span>
                </div>
              )}
              {claim.closed_at && <div className="text-xs text-slate-400">Clôturé le {formatDateFR(claim.closed_at.substring(0, 10))}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ l, v }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate-500">{l}</span>
      <span className="font-medium text-slate-900 text-right">{v}</span>
    </div>
  );
}
